import { authenticatedApiClient } from '../axios'
import type {
  CommentModerationResult,
  CreatePlaceCommentInput,
  CreatePlaceCommentResult,
  CreatePlaceInput,
  Place,
  PlaceCategory,
  PlaceComment,
  PlaceReactionPerson,
  PlacesFilter,
  ReportPlaceInput,
  ShareToFeedInput,
} from '@cobu/types'
import { isCommentModeration } from '@cobu/types'
import { PLACE_COMMENT_ENDPOINT, PLACE_ENDPOINT } from '../config/api'

/**
 * Places is fully wired to the real emperor backend. The one thing still
 * stubbed is comment heart-reactions (no backend), which lives in the UI, not
 * here — this service has no mock left.
 *
 * Note on names: the backend never sends a full last name. Place attribution
 * arrives pre-formatted as `addedByName` ("First L.") and comment authors as
 * `authorName` — both are rendered as-is, no client-side splitting.
 */

/** Wire shape of a place from GET/POST /place and GET /place/:id. */
interface ApiPlace {
  uuid: string
  building: string
  createdBy: string
  name: string
  category: PlaceCategory
  mapsLink?: string
  street?: string
  city?: string
  zip?: string
  description?: string
  isClosed: boolean
  addedByName: string
  createdAt: string
  updatedAt: string
  likeCount: number
  beenCount: number
  commentCount: number
  myLike: boolean
  myBeen: boolean
  distanceFromBuilding?: number | null
}

/**
 * A blocked comment comes back on the same 200 as a successful one, carrying
 * `moderationType` where the comment fields would be — so the create calls have
 * to sniff the body rather than rely on the status code.
 */
type ApiCreateCommentResponse = ApiPlaceComment & Partial<CommentModerationResult>

/** Wire shape of a place comment (flat — no threading). */
interface ApiPlaceComment {
  uuid: string
  place: string
  user: string
  authorName: string
  contents: string
  createdAt: string
  updatedAt: string
  reactionsCount: number
  myReaction: string | null
}

/** Response of the like/been toggle endpoints. */
export interface LikeToggleResult {
  likeCount: number
  myLike: boolean
}
export interface BeenToggleResult {
  beenCount: number
  myBeen: boolean
}

/** Response of the place-comment react/unreact endpoints. */
export interface CommentReactionResult {
  reactionsCount: number
  myReaction: string | null
}

/**
 * Thrown by createPlace when the server rejects a create as a duplicate (same
 * name + address in the building) with a 400. Carries the already-existing place
 * so the add screen can offer "view it" / "Add anyway" (resend with
 * confirmDuplicate: true). Any other error propagates as the raw AxiosError.
 */
export class DuplicatePlaceError extends Error {
  existingPlace: Place
  constructor(message: string, existingPlace: Place) {
    super(message)
    this.name = 'DuplicatePlaceError'
    this.existingPlace = existingPlace
  }
}

const mapApiPlaceToPlace = (api: ApiPlace): Place => ({
  uuid: api.uuid,
  building: api.building,
  createdBy: api.createdBy,
  name: api.name,
  category: api.category,
  street: api.street ?? '',
  city: api.city ?? '',
  zip: api.zip ?? '',
  mapsLink: api.mapsLink,
  distanceFromBuilding: api.distanceFromBuilding ?? null,
  description: api.description,
  addedByName: api.addedByName,
  createdAt: api.createdAt,
  updatedAt: api.updatedAt,
  isClosed: api.isClosed,
  likeCount: api.likeCount,
  beenCount: api.beenCount,
  commentCount: api.commentCount,
  myLike: api.myLike,
  myBeen: api.myBeen,
})

const mapApiComment = (api: ApiPlaceComment): PlaceComment => ({
  uuid: api.uuid,
  place: api.place,
  user: api.user,
  authorName: api.authorName,
  contents: api.contents,
  createdAt: api.createdAt,
  updatedAt: api.updatedAt,
  reactionsCount: api.reactionsCount ?? 0,
  myReaction: api.myReaction ?? null,
})

export class PlacesService {
  // Browse. `buildingId` is derived server-side from the auth token; it's kept
  // in the signature (and the react-query key) only to scope the cache per user.
  // Hidden/closed places are excluded server-side. Paginated via 1-indexed page.
  async getPlaces(_buildingId: string, filter: PlacesFilter = {}, page = 1): Promise<Place[]> {
    const apiPlaces = await authenticatedApiClient.get<ApiPlace[]>(PLACE_ENDPOINT, {
      params: { category: filter.category, sort: filter.sort, page },
    })
    return apiPlaces.map(mapApiPlaceToPlace)
  }

  // Detail. A 404 means "not visible to you" (missing, another community, or
  // hidden) — never retried as a transient error; react-query surfaces it and
  // the screen shows a not-found state.
  async getPlace(placeId: string): Promise<Place> {
    const apiPlace = await authenticatedApiClient.get<ApiPlace>(`${PLACE_ENDPOINT}/${placeId}`)
    return mapApiPlaceToPlace(apiPlace)
  }

  // Add-a-place. building and attribution are server-derived, so `_buildingId`
  // is unused — kept only for call-site stability. The server requires either
  // mapsLink or street (the add form always sends street).
  async createPlace(_buildingId: string, input: CreatePlaceInput): Promise<Place> {
    try {
      const apiPlace = await authenticatedApiClient.post<ApiPlace>(PLACE_ENDPOINT, {
        name: input.name,
        category: input.category,
        street: input.street,
        city: input.city,
        zip: input.zip,
        description: input.description,
        // Only sent on "Add anyway" — tells the server to skip the dup check.
        ...(input.confirmDuplicate ? { confirmDuplicate: true } : {}),
      })
      return mapApiPlaceToPlace(apiPlace)
    } catch (error: any) {
      // 400 with an existingPlace body means the server blocked a duplicate.
      const data = error?.response?.data
      if (error?.response?.status === 400 && data?.existingPlace) {
        throw new DuplicatePlaceError(
          data.message ?? 'A place with this name and address already exists',
          mapApiPlaceToPlace(data.existingPlace as ApiPlace),
        )
      }
      throw error
    }
  }

  // Boolean per-user like toggle. Both directions are idempotent server-side and
  // return the authoritative { likeCount, myLike } for optimistic reconciliation.
  async toggleLike(placeId: string, liked: boolean): Promise<LikeToggleResult> {
    if (liked) {
      return authenticatedApiClient.post<LikeToggleResult>(`${PLACE_ENDPOINT}/${placeId}/like`)
    }
    return authenticatedApiClient.delete<LikeToggleResult>(`${PLACE_ENDPOINT}/${placeId}/like`)
  }

  async toggleBeen(placeId: string, been: boolean): Promise<BeenToggleResult> {
    if (been) {
      return authenticatedApiClient.post<BeenToggleResult>(`${PLACE_ENDPOINT}/${placeId}/been`)
    }
    return authenticatedApiClient.delete<BeenToggleResult>(`${PLACE_ENDPOINT}/${placeId}/been`)
  }

  // The neighbors who liked / have "been" — for the people-list bottom sheet the
  // like/been counts open. Names arrive pre-formatted "First L." (no unit
  // numbers) and sorted newest-first server-side, so we render them as-is.
  async getReactionPeople(placeId: string, type: 'like' | 'been'): Promise<PlaceReactionPerson[]> {
    return authenticatedApiClient.get<PlaceReactionPerson[]>(`${PLACE_ENDPOINT}/${placeId}/reaction`, {
      params: { type },
    })
  }

  // Flat comment list, 1-indexed page; page size is fixed server-side.
  async getComments(placeId: string, page = 1): Promise<PlaceComment[]> {
    const apiComments = await authenticatedApiClient.get<ApiPlaceComment[]>(
      PLACE_COMMENT_ENDPOINT(placeId),
      { params: { page } },
    )
    return apiComments.map(mapApiComment)
  }

  // Moderation runs server-side on every comment (curated blocklist, then AWS
  // Comprehend profanity + bilingual sentiment). A blocked comment is NOT an
  // error status — it's a 200 whose body carries `moderationType` and no comment,
  // so callers must check via isCommentModeration() before using the result.
  //
  // The auto-hide case is invisible here on purpose: a comment the server judged
  // strongly negative is created with hidden: true and returned as a normal
  // comment, then simply omitted from the list on refetch.
  async createComment(input: CreatePlaceCommentInput): Promise<CreatePlaceCommentResult> {
    const response = await authenticatedApiClient.post<ApiCreateCommentResponse>(
      PLACE_COMMENT_ENDPOINT(input.place),
      {
        contents: input.contents,
        ...(input.userDecision ? { userDecision: input.userDecision } : {}),
      },
    )
    if (isCommentModeration(response as CommentModerationResult)) {
      return {
        moderationType: response.moderationType!,
        confidence: response.confidence,
        flaggedTerms: response.flaggedTerms,
      }
    }
    return mapApiComment(response)
  }

  // Author-or-admin only; the server 403s otherwise.
  async deleteComment(placeId: string, commentId: string): Promise<void> {
    await authenticatedApiClient.delete(`${PLACE_COMMENT_ENDPOINT(placeId)}/${commentId}`)
  }

  // One reaction per user per comment (shared Reaction table with Feed). POSTing
  // a different name swaps it; DELETE removes it. Both return the authoritative
  // { reactionsCount, myReaction } for optimistic reconciliation.
  async reactToComment(placeId: string, commentId: string, reactionName: string): Promise<CommentReactionResult> {
    return authenticatedApiClient.post<CommentReactionResult>(
      `${PLACE_COMMENT_ENDPOINT(placeId)}/${commentId}/react`,
      { reactionName },
    )
  }

  async unreactToComment(placeId: string, commentId: string): Promise<CommentReactionResult> {
    return authenticatedApiClient.delete<CommentReactionResult>(
      `${PLACE_COMMENT_ENDPOINT(placeId)}/${commentId}/react`,
    )
  }

  async reportPlace(input: ReportPlaceInput): Promise<void> {
    await authenticatedApiClient.post(`${PLACE_ENDPOINT}/${input.place}/report`, {
      reason: input.reason,
    })
  }

  // Reuses the existing Feed stack: a normal group comment carrying the place
  // fields the backend now accepts. Reactions/replies come for free. Auto-shares
  // from the add flow omit shareType, so placePostType is left off entirely
  // (no type badge is associated with the post).
  //
  // Inherits the Feed's moderation contract: a blocked share returns a 200 whose
  // body is `{ moderationType, ... }`, so the caller must not report success
  // until it has checked. Resolves to null when the post went through.
  async shareToFeed(input: ShareToFeedInput): Promise<CommentModerationResult | null> {
    const response = await authenticatedApiClient.post<Partial<CommentModerationResult>>(
      '/v2/groupcomment',
      {
        contents: input.comment,
        place: input.place,
        ...(input.shareType ? { placePostType: input.shareType } : {}),
        ...(input.userDecision ? { userDecision: input.userDecision } : {}),
      },
    )
    return isCommentModeration(response as CommentModerationResult)
      ? {
          moderationType: response.moderationType!,
          confidence: response.confidence,
          flaggedTerms: response.flaggedTerms,
        }
      : null
  }
}

const placesService = new PlacesService()
export default placesService
